import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useScaleReader } from './useScaleReader';

import { Badge } from '@/shared/ui/badge';
import { Input } from '@/shared/ui/input';

export type WeightInputMethod = 'scale' | 'typed';

interface ScaleAwareWeightInputProps {
  id?: string;
  value: string;
  disabled?: boolean;
  placeholder?: string;
  /** Fires on every value change, tagging whether it came from the scale or manual typing. */
  onValueChange: (next_value: string, method: WeightInputMethod) => void;
}

const HOLD_KEY = 'Control';

/**
 * Reusable weight input for the POS: a normal editable field, but while the
 * user holds Ctrl AND the field has focus, it mirrors live readings from the
 * Rhino I-QY scale (via `useScaleReader` -> local `@novapet/scale-bridge`
 * process). Releasing the key freezes whatever value was last shown — the
 * field then behaves exactly like a plain input again, editable as usual.
 * Connection loss never blocks typing; it just flips the status badge.
 */
export function ScaleAwareWeightInput({
  id,
  value,
  disabled,
  placeholder,
  onValueChange,
}: ScaleAwareWeightInputProps): JSX.Element {
  const { t } = useTranslation();
  const scale = useScaleReader();
  const [is_focused, setIsFocused] = useState(false);

  useEffect(() => {
    if (scale.live && is_focused && scale.weight_kg !== null) {
      onValueChange(scale.weight_kg.toFixed(1), 'scale');
    }
    // Only the arrival of a new reading should trigger this — `is_focused`/`live`
    // are read, not depended on, so releasing the key doesn't re-fire it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale.weight_kg]);

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Input
          id={id}
          type="text"
          inputMode="decimal"
          value={value}
          onChange={(e) => onValueChange(e.target.value, 'typed')}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false);
            scale.stopLive();
          }}
          onKeyDown={(e) => {
            if (e.key === HOLD_KEY) scale.startLive();
          }}
          onKeyUp={(e) => {
            if (e.key === HOLD_KEY) scale.stopLive();
          }}
          disabled={disabled}
          placeholder={placeholder}
        />
        <Badge variant={scale.connected ? 'success' : 'muted'} className="whitespace-nowrap text-xs">
          {scale.connected ? t('doctor_pos.scale_connected') : t('doctor_pos.scale_disconnected')}
        </Badge>
      </div>
      <p className="text-xs text-muted-foreground">{t('doctor_pos.scale_hold_hint')}</p>
      {scale.error ? (
        <p className="text-xs text-destructive">{t(`doctor_pos.${scale.error}`)}</p>
      ) : null}
    </div>
  );
}
